<script setup lang="ts">
import { useModyApi } from '~/middelware/ModyApi'
import type { Product } from '~/utils/Product'

const api = useModyApi()
const featuredSets = ref<Product[]>([])
const featuredSetsLoading = ref(true)
const featuredSetsUnavailable = ref(false)

const mainCategories = [
  {
    title: 'Bühne & StageTruck',
    label: 'Bühne',
    description: 'Mobile Bühne für Outdoor-Events, Konzerte und Veranstaltungen mit kurzer Aufbauzeit.',
    image: '/stagetruck-mauterndorf-original.jpg',
    imageAlt: 'StageTruck von MODY beim Mittelalterfest in Mauterndorf',
    to: '/shop/category/stagetruck',
  },
  {
    title: 'Tontechnik',
    label: 'Sound',
    description: 'Klare Sprache, druckvoller Sound und passende Anlagen für jede Veranstaltungsgröße.',
    image: '/Ton.jpg',
    to: '/shop/category/tontechnik',
  },
  {
    title: 'Lichttechnik',
    label: 'Licht',
    description: 'Stimmungsvolle Beleuchtung, Effekte und professionelle Lichttechnik für dein Event.',
    image: '/Licht.png',
    to: '/shop/category/lichttechnik',
  },
  {
    title: 'Setangebote',
    label: 'Komplettlösungen',
    description: 'Vorkonfigurierte Pakete für Partys und kleinere Events – unkompliziert und schnell einsatzbereit.',
    image: '/landingpages/dj-set1/DJ_Set_1_4.JPG',
    to: '/shop/category/setangebote',
  },
]

const eventEntries = [
  {
    title: 'Hochzeit & private Feier',
    description: 'Kompakte Ton- und Lichtsets für Musik, Reden und eine volle Tanzfläche.',
    icon: 'pi-heart',
    to: '/shop/category/setangebote',
    linkLabel: 'Passende Sets ansehen',
  },
  {
    title: 'Firmenfeier & Vortrag',
    description: 'Sprachverständlichkeit, Funkmikrofone und zuverlässige Technik für Präsentationen.',
    icon: 'pi-building',
    to: '/shop/category/tontechnik',
    linkLabel: 'Zur Tontechnik',
  },
  {
    title: 'Konzert & Vereinsfest',
    description: 'Bühne, Beschallung und Licht für Live-Programm und größere Publikumsflächen.',
    icon: 'pi-megaphone',
    to: '/shop/category/stagetruck',
    linkLabel: 'Bühnenlösungen ansehen',
  },
]

onMounted(loadFeaturedSets)

async function loadFeaturedSets() {
  featuredSetsLoading.value = true
  featuredSetsUnavailable.value = false

  try {
    const timeout = new Promise<never>((_, reject) => {
      window.setTimeout(() => reject(new Error('Setangebote konnten nicht rechtzeitig geladen werden.')), 6000)
    })
    const products = await Promise.race([api.getCategoryProducts('setangebote'), timeout])
    featuredSets.value = products.filter(product => product.Public).slice(0, 3)
    featuredSetsUnavailable.value = featuredSets.value.length === 0
  }
  catch {
    featuredSetsUnavailable.value = true
  }
  finally {
    featuredSetsLoading.value = false
  }
}
</script>

<template>
  <main class="shop-landing">
    <section class="shop-section category-section" aria-labelledby="category-heading">
      <div class="section-heading">
        <h2 id="category-heading">Was brauchst du für dein Event?</h2>
        <p>Wähle einen Bereich oder starte weiter unten mit der Art deiner Veranstaltung.</p>
      </div>

      <div class="category-grid">
        <NuxtLink v-for="category in mainCategories" :key="category.to" :to="category.to" class="category-card">
          <NuxtImg
            :src="category.image"
            :alt="category.imageAlt || `${category.title} von Mody Veranstaltungstechnik`"
            width="900"
            height="620"
            loading="lazy"
          />
          <div class="category-card__overlay"></div>
          <div class="category-card__content">
            <p>{{ category.label }}</p>
            <h3>{{ category.title }}</h3>
            <span>{{ category.description }}</span>
            <strong>Bereich ansehen <i class="pi pi-arrow-right" aria-hidden="true"></i></strong>
          </div>
        </NuxtLink>
      </div>
    </section>

    <section class="shop-section event-section" aria-labelledby="event-heading">
      <div class="section-heading section-heading--left">
        <h2 id="event-heading">Noch nicht sicher, welche Technik passt?</h2>
        <p>Diese Einstiege führen dich zu einer sinnvollen Auswahl für typische Veranstaltungsformate.</p>
      </div>

      <div class="event-grid">
        <NuxtLink v-for="event in eventEntries" :key="event.title" :to="event.to" class="event-card">
          <i :class="['pi', event.icon]" aria-hidden="true"></i>
          <div>
            <h3>{{ event.title }}</h3>
            <p>{{ event.description }}</p>
            <strong>{{ event.linkLabel }} <i class="pi pi-arrow-right" aria-hidden="true"></i></strong>
          </div>
        </NuxtLink>
      </div>
    </section>

    <section class="shop-section set-section" aria-labelledby="set-heading">
      <div class="section-heading section-heading--split">
        <div>
          <h2 id="set-heading">Beliebte Setangebote</h2>
          <p>Fertige Kombinationen für alle, die ohne lange Planung zur passenden Technik kommen möchten.</p>
        </div>
        <NuxtLink to="/shop/category/setangebote" class="text-link">Alle Setangebote <i class="pi pi-arrow-right"></i></NuxtLink>
      </div>

      <div v-if="featuredSetsLoading" class="set-grid" aria-label="Setangebote werden geladen" aria-busy="true">
        <div v-for="index in 3" :key="index" class="set-card set-card--skeleton">
          <Skeleton height="210px" />
          <Skeleton width="45%" height="0.8rem" />
          <Skeleton width="82%" height="1.35rem" />
          <Skeleton width="35%" height="1rem" />
        </div>
      </div>

      <div v-else-if="featuredSets.length" class="set-grid">
        <NuxtLink
          v-for="product in featuredSets"
          :key="product.id"
          :to="`/shop/product/${product.slug}`"
          class="set-card"
        >
          <div class="set-card__image">
            <NuxtImg
              v-if="product.images[0]?.rel_url"
              provider="strapi"
              :src="product.images[0].rel_url"
              :alt="product.images[0].alt || product.Name"
              width="720"
              height="480"
              loading="lazy"
            />
            <NuxtImg v-else src="/landingpages/dj-set1/DJ_Set_1_4.JPG" :alt="product.Name" width="720" height="480" loading="lazy" />
          </div>
          <div class="set-card__content">
            <div class="set-card__badges">
              <span v-for="badge in product.badges.slice(0, 2)" :key="badge.label">{{ badge.label }}</span>
            </div>
            <h3>{{ product.Name }}</h3>
            <p v-if="product.Price">ab {{ product.Price.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' }) }}</p>
            <p v-else>Preis auf Anfrage</p>
            <strong>Details ansehen <i class="pi pi-arrow-right" aria-hidden="true"></i></strong>
          </div>
        </NuxtLink>
      </div>

      <div v-else-if="featuredSetsUnavailable" class="set-fallback" role="status">
        <div>
          <h3>Unsere Komplettlösungen sind weiterhin erreichbar.</h3>
          <p>Die einzelnen Sets konnten gerade nicht geladen werden. In der Kategorie findest du die aktuelle Auswahl.</p>
          <NuxtLink to="/shop/category/setangebote" class="primary-link">Setangebote öffnen <i class="pi pi-arrow-right"></i></NuxtLink>
        </div>
        <NuxtImg src="/landingpages/dj-set1/DJ_Set_1_4.JPG" alt="DJ-Set mit Ton- und Lichttechnik" width="900" height="620" loading="lazy" />
      </div>
    </section>

    <section class="shop-consultation">
      <div>
        <h2>Du möchtest dich nicht durch das Sortiment arbeiten?</h2>
        <p>Beschreibe kurz dein Event – wir stellen dir eine passende Lösung zusammen.</p>
      </div>
      <NuxtLink to="/shop/request" class="primary-link">Unverbindlich anfragen <i class="pi pi-arrow-right"></i></NuxtLink>
    </section>
  </main>
</template>

<style scoped lang="scss">
.shop-landing{
  padding-bottom: clamp(4rem, 8vw, 7rem);
  color: white;
  background: #101011;
}

.shop-section{
  width: min(100%, 1120px);
  margin: 0 auto;
  padding: clamp(2.75rem, 5vw, 4.75rem) 1.5rem;
}

.section-heading{
  max-width: 760px;
  margin: 0 auto 1.75rem;
  text-align: center;

  h2{
    margin: 0;
    font-size: clamp(1.9rem, 3.5vw, 2.8rem);
    font-weight: 600;
    line-height: 1.08;
    letter-spacing: -0.025em;
  }

  > p:last-child,
  > div > p:last-child{
    margin: 1rem 0 0;
    color: rgb(168 162 158);
    font-size: 1.05rem;
    line-height: 1.6;
  }
}

.section-heading--left{
  margin-inline: 0;
  text-align: left;
}

.section-heading--split{
  max-width: none;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  text-align: left;

  > div{
    max-width: 720px;
  }
}

.category-grid{
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.category-card{
  position: relative;
  min-height: 390px;
  overflow: hidden;
  color: white;
  background: #1b1c1f;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: $public-radius-large;
  text-decoration: none;

  > img{
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 350ms ease;
  }

  &:hover > img,
  &:focus-visible > img{
    transform: scale(1.02);
  }

  &:focus-visible{
    outline: 3px solid #E39737;
    outline-offset: 3px;
  }
}

.category-card__overlay{
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(7, 7, 8, 0.08) 15%, rgba(7, 7, 8, 0.94) 92%);
}

.category-card__content{
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  padding: clamp(1.5rem, 4vw, 2.2rem);

  > p{
    margin: 0 0 0.45rem;
    color: $public-text-muted;
    font-size: 0.75rem;
    font-weight: 600;
  }

  h3{
    margin: 0;
    font-size: clamp(1.6rem, 3vw, 2.25rem);
    line-height: 1.08;
  }

  > span{
    max-width: 500px;
    margin-top: 0.75rem;
    display: block;
    color: rgb(214 211 209);
    line-height: 1.5;
  }

  strong{
    margin-top: 1.2rem;
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 0.9rem;
  }
}

.event-section{
  width: 100%;
  max-width: none;
  padding-inline: max(1.5rem, calc((100vw - 1120px) / 2));
  background: #171719;
  border-block: 1px solid rgba(255, 255, 255, 0.055);
}

.event-grid{
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.event-card{
  min-height: 270px;
  padding: 1.6rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: white;
  background: #202124;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: $public-radius-card;
  text-decoration: none;
  transition: border-color 160ms ease, transform 160ms ease;

  > i{
    width: 46px;
    height: 46px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #E39737;
    background: rgba(227, 151, 55, 0.12);
    border-radius: $public-radius-control;
    font-size: 1.2rem;
  }

  h3{
    margin: 1.7rem 0 0;
    font-size: 1.35rem;
  }

  p{
    margin: 0.75rem 0 0;
    color: rgb(168 162 158);
    line-height: 1.55;
  }

  strong{
    margin-top: 1.25rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #E39737;
    font-size: 0.86rem;
  }

  &:hover,
  &:focus-visible{
    border-color: rgba(227, 151, 55, 0.45);
    background: #222326;
  }

  &:focus-visible{
    outline: 2px solid #E39737;
    outline-offset: 3px;
  }
}

.set-grid{
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.set-card{
  overflow: hidden;
  color: white;
  background: #191a1d;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: $public-radius-card;
  text-decoration: none;

  &:hover,
  &:focus-visible{
    border-color: rgba(227, 151, 55, 0.4);
  }

  &:focus-visible{
    outline: 2px solid #E39737;
    outline-offset: 3px;
  }
}

.set-card__image{
  height: 210px;
  background: #222326;

  img{
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.set-card__content{
  padding: 1.35rem;

  h3{
    min-height: 2.8em;
    margin: 0.65rem 0 0;
    font-size: 1.2rem;
    line-height: 1.35;
  }

  > p{
    margin: 0.7rem 0 0;
    color: rgb(214 211 209);
  }

  strong{
    margin-top: 1.3rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #E39737;
    font-size: 0.88rem;
  }
}

.set-card__badges{
  min-height: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;

  span{
    padding: 0.24rem 0.48rem;
    color: rgb(214 211 209);
    background: #292a2e;
    border-radius: 999px;
    font-size: 0.7rem;
  }
}

.set-card--skeleton{
  padding: 0.9rem;
  display: grid;
  gap: 0.9rem;
}

.set-fallback{
  min-height: 330px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: #191a1d;
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: $public-radius-large;

  > div{
    padding: clamp(2rem, 5vw, 3.5rem);
    align-self: center;
  }

  h3{
    margin: 0;
    font-size: clamp(1.7rem, 3vw, 2.4rem);
    line-height: 1.15;
  }

  > div > p:not(.section-eyebrow){
    margin: 1rem 0 1.5rem;
    color: rgb(168 162 158);
    line-height: 1.6;
  }

  > img{
    width: 100%;
    height: 100%;
    min-height: 330px;
    object-fit: cover;
  }
}

.text-link,
.primary-link{
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}

.text-link{
  color: #E39737;
}

.primary-link{
  min-height: 48px;
  padding: 0.7rem 1.2rem;
  color: #111214;
  background: #E39737;
  border-radius: $public-radius-control;

  &:hover,
  &:focus-visible{
    background: #efa94d;
  }

  &:focus-visible{
    outline: 2px solid white;
    outline-offset: 3px;
  }
}

.shop-consultation{
  width: min(calc(100% - 3rem), 1120px);
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  background: $public-surface;
  border: 1px solid $public-border-strong;
  border-radius: $public-radius-large;

  h2{
    max-width: 720px;
    margin: 0;
    font-size: clamp(1.7rem, 3.2vw, 2.45rem);
    line-height: 1.12;
  }

  > div > p:last-child{
    margin: 0.9rem 0 0;
    color: rgb(168 162 158);
    line-height: 1.55;
  }
}

@media (max-width: 767px){
  .category-grid,
  .event-grid,
  .set-grid,
  .set-fallback{
    grid-template-columns: 1fr;
  }

  .category-card{
    min-height: 360px;
  }

  .section-heading--split,
  .shop-consultation{
    align-items: flex-start;
    flex-direction: column;
  }

  .set-fallback > img{
    min-height: 240px;
  }
}
</style>
